import { useAtom } from 'jotai'
import { timelineContainerRefAtom } from '../atoms/timelineUi.atom'
import { timelineMinimumsAtom } from '../atoms/timeline.atom'
import reverse from 'lodash/fp/reverse'
import { useMemo } from 'react'
import { useScrollTimeline } from '../hooks/useScrollTimeline'
import { LeftLink, RightLink } from './ScheduleLinks'
import { Flex } from '@chakra-ui/react'
import {
  TIMELINE_LINK_FADE_OFFSET,
  TIMELINE_PRODUCT_ROW_HEIGHT_PX
} from '../timeline.constants'

const useMinimumLinks = () => {
  const [minimumsTimeline] = useAtom(timelineMinimumsAtom)
  const [timelineContainerRef] = useAtom(timelineContainerRefAtom)
  const timelineScroll = useScrollTimeline()

  const currentTimelineContainerWidth = timelineContainerRef?.clientWidth ?? 0

  const reversedMinimums = reverse(minimumsTimeline)

  /**
   * Get the next/latest minimums which are not shown in the timeline
   * viewport right now.
   */
  const lastPastOffscreenMinimum = reversedMinimums.find(
    m => m.endDate.offset < timelineScroll.timelineScrollPosition
  )

  const firstFutureOffscreenMinimum = minimumsTimeline.find(
    p =>
      p.startDate.offset >
      timelineScroll.timelineScrollPosition + currentTimelineContainerWidth
  )

  const showLeftLink = useMemo(() => {
    if (!lastPastOffscreenMinimum) {
      return false
    }

    /**
     * As we approach the point of revealing the minimum through normal
     * scrolling, we want to hide it to avoid overlapping the link
     * and the underling label.
     */
    if (
      lastPastOffscreenMinimum.endDate.offset >
      timelineScroll.timelineScrollPosition
    ) {
      return false
    }

    const minimumIndex = minimumsTimeline.findIndex(
      p => p.id === lastPastOffscreenMinimum.id
    )
    const minimumAfter = minimumsTimeline[minimumIndex + 1]

    /**
     * As we approach the next minimum's posiition, we also want to hide the link, so
     * it doesn't overlap with that one, either.
     */
    if (
      minimumAfter &&
      minimumAfter.startDate.offset <
        timelineScroll.timelineScrollPosition + TIMELINE_LINK_FADE_OFFSET + 100
    ) {
      return false
    }

    return true
  }, [
    lastPastOffscreenMinimum,
    minimumsTimeline,
    timelineScroll.timelineScrollPosition
  ])

  const showRightLink = useMemo(() => {
    const currentContainerWidth = timelineContainerRef?.clientWidth ?? 0
    if (!firstFutureOffscreenMinimum) {
      return false
    }

    /**
     * As we approach the point of revealing the minimum label via scrolling
     * to the right, we want to hide the link to avoid overlapping the label.
     */
    if (
      firstFutureOffscreenMinimum.startDate.offset <
      timelineScroll.timelineScrollPosition + currentContainerWidth
    ) {
      return false
    }

    const minimumIndex = minimumsTimeline.findIndex(
      p => p.id === firstFutureOffscreenMinimum.id
    )
    const minimumBefore = minimumsTimeline[minimumIndex - 1]

    /**
     * We also want to avoid overlapping the previous minimum's label as we
     * scroll to the left.
     */
    if (
      minimumBefore &&
      minimumBefore.endDate.offset >
        timelineScroll.timelineScrollPosition + currentContainerWidth - 500
    ) {
      return false
    }

    return true
  }, [
    firstFutureOffscreenMinimum,
    minimumsTimeline,
    timelineScroll.timelineScrollPosition,
    timelineContainerRef?.clientWidth
  ])

  return {
    scheduleHasMinimums: minimumsTimeline.length > 0,
    left: {
      showLink: showLeftLink,
      onClick: () => {
        if (!showLeftLink || !lastPastOffscreenMinimum) {
          return
        }

        timelineScroll.scrollTimeline({
          left:
            lastPastOffscreenMinimum?.startDate.offset -
            TIMELINE_LINK_FADE_OFFSET
        })
      },
      label: 'Minimum commitment',
      summary: lastPastOffscreenMinimum?.summary
    },
    right: {
      showLink: showRightLink,
      onClick: () => {
        if (!showRightLink || !firstFutureOffscreenMinimum) {
          return
        }

        timelineScroll.scrollTimeline({
          left:
            firstFutureOffscreenMinimum?.startDate.offset -
            TIMELINE_LINK_FADE_OFFSET
        })
      },
      label: 'Minimum commitment',
      summary: firstFutureOffscreenMinimum?.summary
    }
  }
}

export const MinimumLeftLinks = () => {
  const {
    scheduleHasMinimums,
    left: { showLink, onClick, label, summary }
  } = useMinimumLinks()

  if (!scheduleHasMinimums) {
    return null
  }

  return (
    <Flex
      height={`${TIMELINE_PRODUCT_ROW_HEIGHT_PX}px`}
      position="relative"
      top="-10px"
    >
      <LeftLink
        showLink={showLink}
        onClick={onClick}
        label={label}
        description={`${summary?.amount} / ${summary?.frequency}`}
      />
    </Flex>
  )
}

export const MinimumRightLinks = () => {
  const {
    scheduleHasMinimums,
    right: { showLink, onClick, label, summary }
  } = useMinimumLinks()

  if (!scheduleHasMinimums) {
    return null
  }

  return (
    <Flex
      height={`${TIMELINE_PRODUCT_ROW_HEIGHT_PX}px`}
      justifyContent="flex-end"
      position="relative"
      top="-10px"
    >
      <RightLink
        showLink={showLink}
        onClick={onClick}
        label={label}
        description={`${summary?.amount} / ${summary?.frequency}`}
      />
    </Flex>
  )
}
